// Purpose: Compose a fixed-aspect social image crop while preserving the original.
// Features: Drag/touch/wheel zoom, keyboard movement, restored percentage crops,
// full-image containment, and accessible modal focus/async lifecycle.
// Dependencies: react-easy-crop; the caller sends original bytes and crop to Sharp.
// Important behavior: This editor never rewrites image bytes or chooses output dimensions.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";

const validArea = (area) => area && ["x", "y", "width", "height"].every((key) => Number.isFinite(area[key])) &&
  area.x >= 0 && area.y >= 0 && area.width > 0 && area.height > 0 &&
  area.x + area.width <= 100.000001 && area.y + area.height <= 100.000001;

export default function PromotionImageCropModal({ image, width, height, initialCrop, onApply, onClose, busy = false }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const lockedRef = useRef(false);
  const mountedRef = useRef(true);
  const mediaSizeRef = useRef(null);
  const frameSizeRef = useRef(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialCrop?.zoom || 1);
  const [area, setArea] = useState(undefined);
  const [restoreArea, setRestoreArea] = useState(initialCrop || undefined);
  const [contain, setContain] = useState(initialCrop === null);
  const [resetVersion, setResetVersion] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const locked = busy || saving;
  closeRef.current = onClose;
  lockedRef.current = locked;

  useEffect(() => {
    mountedRef.current = true;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const controls = () => [...(dialogRef.current?.querySelectorAll("button:not(:disabled), input:not(:disabled), [tabindex='0']") || [])];
    controls()[0]?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!lockedRef.current) closeRef.current();
      }
      if (event.key !== "Tab") return;
      const available = controls();
      const first = available[0];
      const last = available[available.length - 1];
      if (!available.length) { event.preventDefault(); dialogRef.current?.focus(); }
      else if (event.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    const keepFocus = (event) => {
      if (!dialogRef.current?.contains(event.target)) (controls()[0] || dialogRef.current)?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", keepFocus);
    return () => {
      mountedRef.current = false;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", keepFocus);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const move = (x, y) => {
    if (!locked && !contain && loaded) setCrop((position) => {
      const next = { x: position.x + x, y: position.y + y };
      // Buttons use rendered coordinates, just like cropper drag gestures.
      // Keep the image covering the frame instead of panning into blank space.
      if (mediaSizeRef.current && frameSizeRef.current) {
        const maxX = Math.max(0, (mediaSizeRef.current.width * zoom - frameSizeRef.current.width) / 2);
        const maxY = Math.max(0, (mediaSizeRef.current.height * zoom - frameSizeRef.current.height) / 2);
        next.x = Math.max(-maxX, Math.min(maxX, next.x));
        next.y = Math.max(-maxY, Math.min(maxY, next.y));
      }
      return next;
    });
  };
  const reset = () => {
    setCrop({ x: 0, y: 0 }); setZoom(1); setArea(undefined); setRestoreArea(undefined);
    setContain(false); setLoaded(false); setError(""); setResetVersion((version) => version + 1);
  };
  const switchMode = () => {
    if (contain) setRestoreArea(validArea(area) ? area : undefined);
    setContain((value) => !value); setLoaded(false); setError("");
  };
  const apply = async () => {
    if (locked || !loaded || (!contain && !validArea(area))) return;
    setSaving(true); setError("");
    try {
      await onApply(contain ? null : { x: area.x, y: area.y, width: area.width, height: area.height, zoom });
    } catch (failure) {
      if (mountedRef.current) setError(failure?.message || "Unable to generate this image. Please try again.");
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  // Manager navigation uses z-index up to 2100. Portal out of route stacking
  // contexts so navigation cannot cover the crop frame or intercept controls.
  return createPortal(<div className="modal d-block" data-testid="promotion-crop-backdrop" style={{ backgroundColor: "rgba(0,0,0,0.65)", zIndex: 2200 }}
    onClick={(event) => { if (event.target === event.currentTarget && !locked) onClose(); }}>
    <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div className="modal-content" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="promotion-crop-title" aria-describedby="promotion-crop-help" tabIndex={-1}>
        <div className="modal-header">
          <h2 className="modal-title h5" id="promotion-crop-title">Crop / Resize Image</h2>
          <button type="button" className="btn-close" aria-label="Close Image Crop" onClick={onClose} disabled={locked} />
        </div>
        <div className="modal-body">
          <p className="fw-semibold">Final image: {width} × {height} px</p>
          <p id="promotion-crop-help">Drag the photo beneath the frame. Use zoom, the mouse wheel, or touch to choose the part to keep. The original stays unchanged.</p>
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          {!loaded && !error && <p role="status">Loading original image...</p>}
          <div className="rounded border mb-3" tabIndex={locked ? -1 : 0} role="group" aria-label="Image crop preview; use arrow keys to move the image"
            style={{ position: "relative", height: "min(52vh, 440px)", minHeight: 240, background: "#242424", overflow: "hidden" }}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget || locked || contain) return;
              const step = event.shiftKey ? 30 : 10;
              const direction = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[event.key];
              if (direction) { event.preventDefault(); move(...direction); }
            }}>
            {contain ? <div style={{ position: "absolute", inset: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ position: "relative", width: `min(100%, calc((max(240px, min(52vh, 440px)) - 24px) * ${width / height}))`, aspectRatio: `${width} / ${height}`, background: "white" }}>
                <img src={image.url} alt="Uncropped original" onLoad={() => { setLoaded(true); setError(""); }} onError={() => { setLoaded(false); setError("The original image could not be loaded. Close and retry, or upload a local copy."); }}
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
              </div>
            </div> : <Cropper key={`${image.url}-${resetVersion}`} image={image.url} aspect={width / height} crop={crop} zoom={zoom} minZoom={1} maxZoom={10}
              initialCroppedAreaPercentages={restoreArea} onCropChange={(position) => { if (!lockedRef.current) setCrop(position); }} onZoomChange={(value) => { if (!lockedRef.current) setZoom(value); }}
              setMediaSize={(size) => { mediaSizeRef.current = size; }} onCropSizeChange={(size) => { frameSizeRef.current = size; }}
              onCropAreaChange={(percentages) => setArea(percentages)} onMediaLoaded={() => { setLoaded(true); setError(""); }}
              mediaProps={{ onError: () => { setLoaded(false); setError("The original image could not be loaded. Close and retry, or upload a local copy."); } }}
              zoomWithScroll={!locked} onInteractionStart={() => setError("")}
              style={{ containerStyle: { pointerEvents: locked ? "none" : undefined }, mediaStyle: { backgroundColor: "#ffffff" } }} />}
          </div>
          <div className="mb-3">
            <label htmlFor="promotion-crop-zoom" className="form-label">Zoom ({zoom.toFixed(2)}×)</label>
            <input id="promotion-crop-zoom" type="range" className="form-range" min="1" max="10" step="0.05" value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))} disabled={locked || contain || !loaded} />
          </div>
          <div className="d-flex flex-wrap gap-2 mb-3" role="group" aria-label="Move image beneath crop frame">
            {[[-10, 0, "Move image left"], [10, 0, "Move image right"], [0, -10, "Move image up"], [0, 10, "Move image down"]].map(([x, y, label]) =>
              <button type="button" key={label} className="btn btn-outline-secondary btn-sm" disabled={locked || contain || !loaded} onClick={() => move(x, y)}>{label}</button>)}
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button type="button" className="btn btn-outline-secondary" disabled={locked} onClick={reset}>Reset crop</button>
            <button type="button" className="btn btn-outline-secondary" disabled={locked} onClick={switchMode}>{contain ? "Crop to fit" : "Use full image"}</button>
          </div>
          {contain && <p className="form-text">The full image is preserved with white padding where the aspect ratios differ.</p>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" disabled={locked} onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={locked || !loaded || (!contain && !validArea(area))} onClick={apply}>{locked ? "Generating image..." : "Generate Sized Image"}</button>
        </div>
      </div>
    </div>
  </div>, document.body);
}
